import { useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { noop, isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { Modal, BusyLoader, Section, Button, Icon } from '@geneui/components';
// Import Components
import DataTable from 'components/common/DataTable';
// Import Hooks
import { useAppSelector } from 'store';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
import { selectCurrencyRates } from 'store/slices/currencyRates';
// Import Services
import { convertRowsToUserCurrency, buildTotalRow, hasCurrencyColumn } from './service';
// Import Constants
import { l, ActionsIcons, ReportPageType } from 'constants/common';

const messageText = {
    [ReportPageType.Segment]: l.SaveSegmentChangeIntoReport,
    [ReportPageType.Report]: l.SaveTheReportAndExecute,
    [ReportPageType.ExclusionRule]: l.SaveTheExclusionRuleWhileLoadingPreviewMessage,
};

const SegmentReportPreviewModal = ({ isVisible, closeHandler, clientsData, isVisibleInfoMessage, pageType }) => {
    const { t } = useTranslation();
    const userCurrencyId = useAppSelector(selectUserSettingsCurrencyId);
    const currencyRates = useAppSelector(selectCurrencyRates)?.[userCurrencyId ?? ''];
    const [data, setData] = useState<Nullable<AnyObject>>(null);
    const [footerValues, setFooterValues] = useState<Nullable<AnyObject>>(null);
    const [loadingText, setLoadingText] = useState(l.WaitDataIsProcessed);

    useEffect(() => {
        if (!isNil(data)) return;

        const timerId = setTimeout(() => setLoadingText(l.DataTooBig), 5000);

        return () => clearTimeout(timerId);
    }, [data]);

    useEffect(() => {
        init();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [clientsData]);

    function init() {
        clientsData && setDataInReportTable(clientsData);
    }

    const setDataInReportTable = (data) => {
        const columns = data.Columns.map((elem) => {
            return {
                text: t(elem.DisplayName),
                sortable: false,
                resizable: false,
                draggable: false,
                dataKey: elem.DisplayName,
            };
        });
        const rows = data.Data.map((elem) => {
            const rowData = {};
            columns.forEach((element, index) => {
                rowData[element.dataKey] = elem[index];
            });
            return {
                actionBar: false,
                dragDisable: true,
                hasHover: true,
                data: rowData,
            };
        });

        if (userCurrencyId && !isEmpty(rows) && hasCurrencyColumn(data.Columns)) {
            const convertedData = convertRowsToUserCurrency(data, currencyRates);

            setFooterValues(
                buildTotalRow(
                    data.Columns,
                    convertedData,
                    userCurrencyId,
                    t(l.TotalOf, { count: convertedData.length }),
                ),
            );
        }

        setData({
            ...data,
            Columns: columns,
            Data: rows,
        });
    };

    return (
        <Modal
            className="crm-segment-report-preview-modal"
            visible={isVisible}
            width="70%"
            title={t(l.Preview)}
            background="dark-background"
            closable={false}
            onCancel={closeHandler}
            footer={<Button onClick={closeHandler}>{t(l.Close)}</Button>}
            closeOnClickOutside={true}
            withPortal={true}
        >
            {!data ? (
                <BusyLoader
                    isBusy={!isVisibleInfoMessage}
                    type="spinner"
                    spinnerSize="big"
                    loadingText={
                        (<span className="preview-modal-loader-message">{t(loadingText)}</span>) as unknown as string
                    }
                >
                    <div className="preview-modal-contents">
                        <Icon type={ActionsIcons.UsedIn} className="preview-modal-info-icon" />
                        <span className="preview-modal-info-message">{t(messageText[pageType])}</span>
                    </div>
                </BusyLoader>
            ) : (
                <Section text={t(l.First100Clients)}>
                    <DataTable
                        withPageSelector={false}
                        isVisibleOnlyDataTable={true}
                        dataCount={data.Data?.length}
                        rowKey="Client Id"
                        columnKey="dataKey"
                        data={data.Data}
                        columns={data.Columns}
                        footerValues={footerValues ?? undefined}
                    />
                </Section>
            )}
        </Modal>
    );
};

SegmentReportPreviewModal.propTypes = {
    clientsData: PropTypes.object,
    pageType: PropTypes.string,
    isVisible: PropTypes.bool,
    closeHandler: PropTypes.func,
    isVisibleInfoMessage: PropTypes.bool,
};

SegmentReportPreviewModal.defaultProps = {
    isVisible: false,
    isVisibleInfoMessage: false,
    closeHandler: noop,
};

export default memo(SegmentReportPreviewModal);
